import {FC} from 'react';
import {useField} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Button} from '@tickchak/react-share/app';
import {TShow} from '../../../../../hooks/useShow';
import {RootState} from '../../../../../redux/store.type';
import {TLecturer} from '../../../../../redux/timetable/timetable.type';
import Ex from '../../../../../assets/icons/ex';
import PlusThin from '../../../../../assets/icons/PlusThin';
import {teamMemberPhoto} from '../../../../../assets/symbols/images/teamMemberPhoto';
import {sortLecturers} from '../../../../../library/sortLecturers';
import * as css from './LecturersSection.css';

const formatLecturerName = (l: TLecturer) => `${l.firstName} ${l.lastName}`.trim();

interface LecturersSectionProps {
    show: TShow;
}

const LecturersSection: FC<LecturersSectionProps> = ({show}) => {
    const {t} = useTranslation('timetable');
    const [field, , helpers] = useField<number[]>('lecturerIds');
    const lecturers = useSelector((state: RootState) => state.timetable.lecturers);

    const selectedLecturers = sortLecturers(lecturers.filter((l: TLecturer) => field.value.includes(l.lid)));

    const removeOne = (e: React.MouseEvent, lid: number) => {
        e.stopPropagation();
        helpers.setValue(field.value.filter(id => id !== lid));
    };

    const isEmpty = selectedLecturers.length === 0;

    return (
        <css.wrap $isEmpty={isEmpty}>
            {selectedLecturers.map((l: TLecturer) => (
                <Button
                    key={l.lid}
                    variant="neutral"
                    size="category"
                    iconEnd={
                        <css.removeIconCircle
                            role="button"
                            aria-label={t('removeLecturer')}
                            onClick={e => removeOne(e, l.lid)}
                        >
                            <Ex color="white" width={7} height={7} />
                        </css.removeIconCircle>
                    }
                    avatar={{src: l.image || teamMemberPhoto, alt: formatLecturerName(l), size: 'xs'}}
                    customStyle={{padding: '0 8px'}}
                >
                    {formatLecturerName(l)}
                </Button>
            ))}
            <Button
                variant="brand"
                size="category"
                iconStart={<PlusThin color="black" />}
                onClick={show.open}
                ariaLabel={t('selectLecturer')}
            >
                {isEmpty ? t('selectLecturer') : null}
            </Button>
        </css.wrap>
    );
};

export default LecturersSection;
